import { usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  useLLMSmartSearchV1,
  SmartSearchQueryTypesEnum,
} from '@ltvco/refresh-lib/hooks';
import { useVehicleValuation } from '@ltvco/refresh-lib/v1';
import { useState, useEffect, useCallback, useRef } from 'react';

import { ERROR_MESSAGES } from '../../constants';
import {
  cleanSearchInput,
  cleanSearchInputWithSpecialCharacters,
  determineSearchType,
  extractVehicleParams,
  formatNumericValue,
  type VehicleSearchResult,
  type VehicleSearchType,
  type VehicleValuationParams,
} from '../../utils';
import { buildRepairEstimateData } from '../utils';

import { useRepairCostEstimatorReportProcessing } from './useRepairCostEstimatorReportProcessing';

import type { AllSearchesSmartSearchResults } from '@ltvco/refresh-lib/api';

interface UseRepairCostEstimatorSearchParams {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
}

interface UseRepairCostEstimatorSearchReturn {
  vinOrVehicleInfo: string;
  location: string;
  repairDescription: string;
  shopQuote: string;
  marketValue: string;
  error: string | null;
  hasSubmitted: boolean;
  hasSubmittedMarketValue: boolean;
  searchType: VehicleSearchType;
  showMarketValueInput: boolean;
  handleVinOrVehicleInfoChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  handleLocationChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
  ) => void;
  handleRepairDescriptionChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  handleShopQuoteChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMarketValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => Promise<void>;
  handleMarketValueSubmit: (
    e?: React.FormEvent<HTMLFormElement>,
  ) => Promise<void>;
}

/**
 * useRepairCostEstimatorSearch - Custom hook to manage repair cost estimator search logic
 * Uses general smart search with context to identify vehicle searches
 * Pattern: similar to VehicleForecasterSearch
 * @hook
 */
export const useRepairCostEstimatorSearch = ({
  onSendMessage,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
}: UseRepairCostEstimatorSearchParams): UseRepairCostEstimatorSearchReturn => {
  const { isFreePlan } = usePermissions();
  const [vinOrVehicleInfo, setVinOrVehicleInfo] = useState('');
  const [location, setLocation] = useState('');
  const [repairDescription, setRepairDescription] = useState('');
  const [shopQuote, setShopQuote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [searchType, setSearchType] = useState<VehicleSearchType>(null);
  const [enableSearch, setEnableSearch] = useState(false);
  const [valuationParams, setValuationParams] =
    useState<VehicleValuationParams | null>(null);
  const [showMarketValueInput, setShowMarketValueInput] = useState(false);
  const [marketValue, setMarketValue] = useState('');
  const [hasSubmittedMarketValue, setHasSubmittedMarketValue] = useState(false);
  const isProcessingRef = useRef(false);

  const {
    data: smartSearchResponse,
    isSuccess,
    isError,
  } = useLLMSmartSearchV1(
    SmartSearchQueryTypesEnum.allReportTypesSmartSearchV1,
    {
      enabled: enableSearch && Boolean(currentInputValue),
      searchValue: currentInputValue,
    },
  );

  const {
    data: fetchedValuation,
    isError: valuationError,
    isSuccess: valuationSuccess,
  } = useVehicleValuation(valuationParams ?? undefined, {
    enabled: Boolean(valuationParams),
  });

  // Reset all form states
  const resetAllStates = useCallback(() => {
    setVinOrVehicleInfo('');
    setLocation('');
    setRepairDescription('');
    setShopQuote('');
    setMarketValue('');
    setError(null);
    setHasSubmitted(false);
    setHasSubmittedMarketValue(false);
    setEnableSearch(false);
    setCurrentInputValue('');
    setSearchType(null);
    setValuationParams(null);
    setShowMarketValueInput(false);
    isProcessingRef.current = false;
    onProcessingStateChange?.(false);
  }, [onProcessingStateChange]);

  const resetInternalState = useCallback(() => {
    setEnableSearch(false);
    setCurrentInputValue('');
    setSearchType(null);
    setValuationParams(null);
    setShowMarketValueInput(false);
    isProcessingRef.current = false;
    onProcessingStateChange?.(false);
  }, [onProcessingStateChange]);

  // Reset internal state AND hasSubmitted when there's an error
  const resetOnError = useCallback(() => {
    resetInternalState();
    setHasSubmitted(false);
  }, [resetInternalState]);

  const { processVehicleReport, pendingData } =
    useRepairCostEstimatorReportProcessing({
      onSendMessage,
      onProcessingComplete: resetInternalState,
      onProcessingError: resetOnError,
      fetchedValuation,
      setValuationParams,
      setError,
      location,
      repairDescription,
      shopQuote,
    });

  // Handle reset when shouldResetInputs becomes true
  useEffect(() => {
    if (shouldResetInputs) {
      resetAllStates();
      onResetComplete();
    }
  }, [shouldResetInputs, resetAllStates, onResetComplete]);

  // Handle valuation error or when valuation returns no data - show market value input
  useEffect(() => {
    if (!valuationParams || !pendingData) return;

    // If valuation query completed (success or error) but no market value, show input
    if (
      (valuationSuccess || valuationError) &&
      !fetchedValuation?.clean_retail?.adjusted &&
      !showMarketValueInput
    ) {
      setShowMarketValueInput(true);
      setValuationParams(null);
      setError(null);
      onProcessingStateChange?.(false);
    }
  }, [
    valuationSuccess,
    valuationError,
    valuationParams,
    fetchedValuation,
    pendingData,
    showMarketValueInput,
    onProcessingStateChange,
  ]);

  useEffect(() => {
    if (
      isProcessingRef.current ||
      !isSuccess ||
      !smartSearchResponse ||
      !currentInputValue
    ) {
      return;
    }

    const response = smartSearchResponse as {
      results: AllSearchesSmartSearchResults;
      error?: string;
    };

    if (response.error) {
      setError(ERROR_MESSAGES.TECHNICAL_DIFFICULTIES);
      resetOnError();
      return;
    }

    const vehicleResults = response.results?.filter(
      (r) => r.type === 'vehicle',
    );

    if (!vehicleResults?.length) {
      setError(ERROR_MESSAGES.INVALID_INPUT);
      resetOnError();
      return;
    }

    const firstResult = vehicleResults[0] as VehicleSearchResult;

    setSearchType(determineSearchType(firstResult));

    const vehicleParams = extractVehicleParams(firstResult);

    isProcessingRef.current = true;
    setCurrentInputValue('');
    processVehicleReport.mutate({ vehicleArgs: vehicleParams });
  }, [
    isSuccess,
    smartSearchResponse,
    currentInputValue,
    processVehicleReport,
    resetOnError,
  ]);

  useEffect(() => {
    if (isError && currentInputValue) {
      setError(ERROR_MESSAGES.TECHNICAL_DIFFICULTIES);
      resetOnError();
    }
  }, [isError, currentInputValue, resetOnError]);

  const handleVinOrVehicleInfoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVinOrVehicleInfo(cleanSearchInput(e.target.value));
      setError(null);
    },
    [],
  );

  const handleLocationChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string | null) => {
      setLocation(value || '');
      setError(null);
    },
    [],
  );

  const handleRepairDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRepairDescription(
        cleanSearchInputWithSpecialCharacters(e.target.value),
      );
      setError(null);
    },
    [],
  );

  const handleShopQuoteChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShopQuote(formatNumericValue(e.target.value));
      setError(null);
    },
    [],
  );

  const handleMarketValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMarketValue(formatNumericValue(e.target.value));
    },
    [],
  );

  const handleMarketValueSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e?.preventDefault();

      if (!marketValue || !pendingData || !onSendMessage) return;

      const numericValue = parseFloat(marketValue.replace(/,/g, ''));
      if (isNaN(numericValue) || numericValue <= 0) {
        setError('Please enter a valid market value');
        return;
      }

      setHasSubmittedMarketValue(true);
      setShowMarketValueInput(false);
      onProcessingStateChange?.(true);

      // Build repair estimate data with manual market value
      const vehicleData = { ...pendingData.vehicleData };
      vehicleData.marketValue = numericValue;
      const message = buildRepairEstimateData(
        isFreePlan,
        vehicleData,
        location,
        repairDescription,
        shopQuote || null,
        pendingData.userMileage,
      );
      onSendMessage(message);
      resetInternalState();
    },
    [
      marketValue,
      pendingData,
      onSendMessage,
      onProcessingStateChange,
      location,
      repairDescription,
      shopQuote,
      resetInternalState,
      isFreePlan,
    ],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e?.preventDefault();

      if (
        !vinOrVehicleInfo ||
        !location ||
        !repairDescription ||
        !onSendMessage
      ) {
        setError('Please fill in all required fields');
        return;
      }

      setError(null);
      setCurrentInputValue(`Vehicle: ${vinOrVehicleInfo}`);
      setHasSubmitted(true);
      setEnableSearch(true);
      onProcessingStateChange?.(true);
    },
    [
      vinOrVehicleInfo,
      location,
      repairDescription,
      onSendMessage,
      onProcessingStateChange,
    ],
  );

  return {
    vinOrVehicleInfo,
    location,
    repairDescription,
    shopQuote,
    marketValue,
    error,
    hasSubmitted,
    hasSubmittedMarketValue,
    searchType,
    showMarketValueInput,
    handleVinOrVehicleInfoChange,
    handleLocationChange,
    handleRepairDescriptionChange,
    handleShopQuoteChange,
    handleMarketValueChange,
    handleSubmit,
    handleMarketValueSubmit,
  };
};
